import { furniture as kinds, C } from "@bd/content";
import { Bitmap, hex, OUTLINE, shade, type RGB } from "./bitmap";
import { spritePainter, T, type IsoPainter, type IsoSprite } from "./iso";
import { leafy } from "./plants";

export const BOXC: RGB[] = [[198, 64, 70], [64, 120, 200], [238, 176, 70], [70, 160, 110], [150, 90, 190], [230, 120, 70], [60, 170, 180], [236, 226, 204]];
const WOOD: RGB = [176, 124, 80];
const DARK: RGB = [110, 70, 46];
const METAL: RGB = [120, 126, 140];
const PAPER: RGB = [244, 240, 228];
const SCREEN: RGB = [150, 220, 250];
const WARM: RGB = [255, 236, 170];
const POT: RGB = [184, 96, 64];

const pickC = (cs: RGB[], i: number): RGB => cs[((i % cs.length) + cs.length) % cs.length];

type Draw = (p: IsoPainter, W: number, H: number, seed: number) => void;
interface Spec {
  z: number;
  draw: Draw;
  light?: { x: number; y: number; z: number; color: RGB; radius: number; strength: number };
}

const legs = (p: IsoPainter, W: number, H: number, z: number, c: RGB) => {
  for (const [x, y] of [[0.5, H - 0.5], [W - 0.5, H - 0.5], [W - 0.5, 0.5]]) p.line([x, y, 0], [x, y, z], shade(c, 0.6));
};

const table = (top: RGB, h = 5, thick = 1.2): Draw => (p, W, H) => {
  legs(p, W, H, h - thick, top);
  p.box(0.3, 0.3, h - thick, W - 0.6, H - 0.6, thick, top);
};

const shelf = (frame: RGB, rows: number, h: number, fill: (p: IsoPainter, x: number, z: number, i: number) => void): Draw => (p, W, H) => {
  const d = Math.min(H, 2.6);
  p.box(0.2, 0.2, 0, W - 0.4, d, h, frame);
  const step = h / rows;
  for (let r = 0; r < rows; r++) for (let k = 0; k < Math.floor((W - 1) / 1.1); k++) fill(p, 0.6 + k * 1.1, r * step + 0.8, r * 7 + k);
  void d;
};

type Fill = (p: IsoPainter, x: number, z: number, i: number) => void;
const booksIn = (pal: RGB[]): Fill => (p, x, z, i) => p.panelY(x, 2.82, z, 0.8, 3.6, pal[i % 8]);
const books = booksIn(BOXC);

// pieces whose game boxes take the decade's box colours (BOXC = the timeless default)
const gameBoxesIn = (pal: RGB[]): Fill => (p, x, z, i) => p.panelY(x, 2.82, z, 0.95, 4, pal[(i * 3) % 8]);
const minisIn = (pal: RGB[]): Fill => (p, x, z, i) => p.box(x, 1.6, z, 0.6, 0.6, 1.8, pal[(i * 5) % 8], { outline: false });
const printRack = (pal: RGB[]): Draw => (p, W, H) => {
  p.box(0.4, 0.4, 0, W - 0.8, H - 0.8, 8, METAL, { top: [160, 166, 180] });
  for (let k = 0; k < 4; k++) p.panelY(1 + k * 2.6, H - 0.39, 1.5, 2, 5, pal[(k + 2) % 8]);
};
const palletOf = (pal: RGB[]): Draw => (p, W, H, seed) => {
  p.box(0.3, 0.3, 0, W - 0.6, H - 0.6, 0.8, [170, 130, 80]);
  for (let lv = 0; lv < 2 + (seed % 2); lv++) for (let a = 0; a < 2; a++) for (let b = 0; b < 2; b++) p.box(0.6 + a * (W / 2 - 0.2), 0.6 + b * (H / 2 - 0.2), 0.8 + lv * 2.6, W / 2 - 0.6, H / 2 - 0.6, 2.6, pal[(a + b + lv + seed) % 7]);
};
const shelfRack = (pal: RGB[]): Draw => (p, W, H) => {
  for (const z of [0, 6, 12]) p.box(0.3, 0.3, z, W - 0.6, Math.min(H, 3) - 0.6, 0.6, [200, 120, 60]);
  for (const x of [0.3, W - 0.6]) p.box(x, 0.3, 0, 0.3, 0.3, 18, [60, 90, 150]);
  for (let k = 0; k < Math.floor(W / 3); k++) for (const z of [0.6, 6.6]) p.box(0.6 + k * 3, 0.6, z, 2.4, 2, 2.4, pal[(k + Math.floor(z)) % 7]);
};
const trolleyOf = (pal: RGB[]): Draw => (p, W, H) => {
  p.box(0.5, 0.5, 1, W - 1, H - 1, 0.6, METAL);
  p.box(1, 1, 1.6, W - 2, H - 2, 2.6, pal[1]);
  p.line([W - 0.6, 0.6, 1], [W - 0.6, 0.6, 7], [60, 60, 70]);
};
const packingTable = (pal: RGB[]): Draw => (p, W, H) => {
  table([200, 170, 130], 5)(p, W, H, 0);
  for (let k = 0; k < 3; k++) p.box(1 + k * 3, 1, 5, 2.4, 2.4, 2.4, pal[(k * 3) % 7]);
};
const conveyorOf = (pal: RGB[]): Draw => (p, W, H, seed) => {
  p.box(0.3, 1, 2, W - 0.6, H - 2, 1.5, [60, 64, 74], { top: [40, 44, 50] });
  for (let k = 0; k < Math.floor(W / 4); k++) p.box(1 + k * 4, 1.4, 3.5, 2.4, H - 2.8, 2, pal[(k + seed) % 7]);
  legs(p, W, H, 2, METAL);
};
const displayStand = (pal: RGB[]): Draw => (p, W, H, seed) => {
  p.box(1, 1, 0, W - 2, H - 2, 4, [236, 232, 220]);
  p.box(1.6, 1.6, 4, W - 3.2, H - 3.2, 4, pal[seed % 8]);
};
const displayCase = (pal: RGB[]): Draw => (p, W, H) => {
  p.box(0.4, 0.4, 0, W - 0.8, H - 0.8, 4, DARK);
  p.box(0.6, 0.6, 4, W - 1.2, H - 1.2, 5, [180, 210, 230], { top: [200, 225, 240] });
  for (let k = 0; k < 3; k++) p.box(1.5 + k * 3, 1.5, 4, 2, 2, 2, pal[k * 2], { outline: false });
};

const plantPot = (r: number, pot: RGB, potH: number): Draw => (p, W, H, seed) => {
  const cx = W / 2;
  const cy = H / 2;
  p.box(cx - 1.4, cy - 1.4, 0, 2.8, 2.8, potH, pot);
  const leaf = leafy(r, seed);
  const [sx, sy] = p.P(cx, cy, potH);
  p.img.blit(leaf, sx - (leaf.w >> 1), sy - leaf.h + 3);
};

const lamp = (h: number, shadeC: RGB): Draw => (p, W, H) => {
  const cx = W / 2;
  const cy = H / 2;
  p.box(cx - 0.8, cy - 0.8, 0, 1.6, 1.6, 0.6, METAL);
  p.line([cx, cy, 0.6], [cx, cy, h], [60, 60, 70]);
  p.box(cx - 1.2, cy - 1.2, h, 2.4, 2.4, 1.6, shadeC, { glowLeft: WARM });
  p.quad([[cx - 0.6, cy - 0.6, h], [cx + 0.6, cy - 0.6, h], [cx + 0.6, cy + 0.6, h], [cx - 0.6, cy + 0.6, h]], WARM, WARM);
};

const machine = (c: RGB, h: number, accent: RGB, glow = true): Draw => (p, W, H) => {
  p.box(0.3, 0.3, 0, W - 0.6, H - 0.6, h, c);
  p.panelY(W * 0.2, H - 0.29, h * 0.45, Math.min(2, W * 0.3), 1.6, accent, glow ? accent : undefined);
  p.box(W * 0.6, H * 0.4, h, W * 0.25, H * 0.3, 0.8, shade(c, 1.2), { outline: false });
};

const stack = (colors: RGB[], levels: number, size = 2.6): Draw => (p, W, H, seed) => {
  for (let lv = 0; lv < levels; lv++) {
    for (let a = 0; a < Math.floor(W / 3); a++) for (let b = 0; b < Math.floor(H / 3); b++) {
      p.box(0.3 + a * 3, 0.3 + b * 3, lv * size, size, size, size, colors[(a + b + lv + seed) % colors.length]);
    }
  }
};

export const FURNITURE: Record<string, Spec> = {
  desk: { z: 12, draw: (p, W, H) => {
    table(WOOD)(p, W, H, 0);
    p.box(1.5, 1, 5, 3, 2.5, 1.5, [60, 64, 74]);
    p.panelY(2, 1.2, 6.5, 2, 2, PAPER);
    for (let k = 0; k < 3; k++) p.floor(5.2 + k * 1.3, 1.5, 1, 1.5, BOXC[k], 5.05);
  } },
  monitor_desk: { z: 13, draw: (p, W, H) => {
    table(WOOD)(p, W, H, 0);
    p.box(3, 0.8, 5, 4, 1, 4, [40, 44, 56]);
    p.panelY(3.3, 1.81, 5.6, 3.4, 3, SCREEN, SCREEN);
  }, light: { x: 5, y: 2, z: 7, color: [0.6, 0.85, 1.0], radius: 6, strength: 0.25 } },
  drawing_board: { z: 12, draw: (p, W, H) => {
    legs(p, W, H, 4, WOOD);
    p.quad([[0.5, 0.5, 4], [W - 0.5, 0.5, 6.5], [W - 0.5, H - 0.5, 6.5], [0.5, H - 0.5, 4]], PAPER);
    p.line([2, 1, 4.3], [W - 2, 1.5, 6], [90, 140, 200]);
  } },
  bookshelf: { z: 20, draw: shelf(DARK, 3, 16, books) },
  corkboard: { z: 14, draw: (p, W) => {
    p.panelY(0.5, 0.4, 4, W - 1, 8, [186, 138, 88]);
    for (let k = 0; k < 4; k++) p.panelY(1 + k * 1.3, 0.38, 6 + (k % 2) * 2, 1, 1.4, [PAPER, [250, 222, 120] as RGB, [160, 210, 236] as RGB, [244, 180, 170] as RGB][k]);
  } },
  whiteboard: { z: 14, draw: (p, W) => {
    p.panelY(0.5, 0.4, 4, W - 1, 8, [246, 246, 240]);
    for (let k = 0; k < 3; k++) p.panelY(1.5 + k * 2.6, 0.38, 7, 1.6, 1.6, [110, 160, 220]);
    p.line([1.5, 0.37, 5.5], [W - 2, 0.37, 5], [220, 80, 80]);
  } },
  plant_pot: { z: 16, draw: plantPot(5, POT, 3) },
  // signature pieces
  design_table: { z: 10, draw: (p, W, H) => {
    legs(p, W, H, 5, DARK);
    p.box(0.4, 0.6, 5, W - 0.8, H - 1.2, 0.8, WOOD);
    // prototypes, cards and a board mid-game
    p.box(1.2, 1.4, 5.8, 3.4, 2.6, 0.3, [236, 226, 204]);
    for (let k = 0; k < 4; k++) p.box(1.6 + k * 0.8, 1.8, 6.1, 0.5, 0.5, 0.6, BOXC[k % 8], { outline: false });
    p.box(W - 4.4, 1.2, 5.8, 1.6, 1.2, 1.2, BOXC[5]);
    p.box(W - 2.6, 2.6, 5.8, 1.2, 0.9, 0.3, PAPER);
  } },
  mural_canvas: { z: 18, draw: (p, W, H) => {
    p.box(0.8, H / 2 - 0.3, 0, 0.6, 0.6, 14, DARK);
    p.box(W - 1.4, H / 2 - 0.3, 0, 0.6, 0.6, 14, DARK);
    p.panelY(0.6, H / 2 + 0.31, 4, W - 1.2, 10, [246, 240, 226]);
    p.panelY(1.4, H / 2 + 0.32, 5, W - 2.8, 3, [90, 150, 210]);
    p.panelY(1.4, H / 2 + 0.32, 8, W - 6, 4, [230, 140, 70]);
    p.panelY(W - 4.6, H / 2 + 0.32, 9, 3, 3, [70, 160, 110]);
  } },
  campaign_board: { z: 16, draw: (p, W, H) => {
    p.box(1, H / 2 - 0.3, 0, 0.6, 0.6, 12, METAL);
    p.box(W - 1.6, H / 2 - 0.3, 0, 0.6, 0.6, 12, METAL);
    p.panelY(0.6, H / 2 + 0.31, 4, W - 1.2, 8, [40, 46, 86]);
    for (let k = 0; k < 3; k++) p.panelY(1.4 + k * 3.4, H / 2 + 0.32, 6, 2.6, 4.5, BOXC[(k + 1) % 8]);
    p.panelY(1.4, H / 2 + 0.32, 4.6, W - 2.8, 0.8, [236, 190, 72]);
  } },
  // rewards for clean sweeps
  golden_meeple: { z: 18, draw: (p, W, H) => {
    p.box(0.8, 0.8, 0, W - 1.6, H - 1.6, 5, [70, 60, 90], { top: [96, 84, 120] });
    const G: RGB = [236, 190, 72];
    p.box(1.8, 2, 5, 2.4, 1.8, 3.5, G, { top: [252, 216, 120] });
    p.box(1.2, 2.2, 6.6, 3.6, 1.4, 1.2, G, { top: [252, 216, 120] });
    p.box(2.2, 2.2, 8.5, 1.6, 1.4, 1.8, G, { top: [255, 230, 150] });
  } },
  hall_of_hits: { z: 16, draw: (p, W) => {
    p.panelY(0.4, 0.42, 3, W - 0.8, 10, [92, 52, 40]);
    for (let k = 0; k < 4; k++) {
      p.panelY(1 + k * 2.7, 0.4, 7.5, 2.1, 1.6, BOXC[k % 8]);
      p.panelY(1 + k * 2.7, 0.39, 4, 2.1, 2.6, [236, 190, 72]);
    }
  } },
  founders_portrait: { z: 16, draw: (p, W) => {
    p.panelY(0.6, 0.42, 3.5, W - 1.2, 9, [176, 132, 60]);
    p.panelY(1.1, 0.41, 4.2, W - 2.2, 7.6, [60, 74, 110]);
    p.panelY(2, 0.4, 5.5, 1.1, 3.4, [232, 196, 160]);
    p.panelY(3.1, 0.4, 5.2, 1.1, 3.6, [210, 170, 130]);
  } },
  lamp_desk: { z: 9, draw: lamp(5, [46, 112, 84]), light: { x: 3, y: 3, z: 6, color: [1.0, 0.85, 0.55], radius: 5, strength: 0.5 } },
  chair: { z: 8, draw: (p, W, H) => {
    legs(p, W, H, 3, DARK);
    p.box(1, 1, 3, W - 2, H - 2, 0.8, [176, 70, 70]);
    p.box(1, 1, 3.8, W - 2, 0.8, 3, [150, 56, 58]);
  } },
  rug: { z: 1, draw: (p, W, H, seed) => {
    const c: RGB = pickC([[124, 40, 54], [70, 120, 120], [60, 74, 110]], seed);
    p.floor(0.5, 0.5, W - 1, H - 1, c);
    p.floor(1.5, 1.5, W - 3, H - 3, shade(c, 1.15));
  } },
  prototype_pile: { z: 8, draw: stack([PAPER, [250, 222, 120], [198, 64, 70]], 2, 2) },
  filing_cabinet: { z: 10, draw: (p, W, H) => {
    p.box(0.8, 0.8, 0, W - 1.6, H - 1.6, 8, [150, 156, 170]);
    for (let k = 0; k < 3; k++) p.line([1.2, H - 0.79, 2 + k * 2.4], [W - 1.2, H - 0.79, 2 + k * 2.4], [90, 96, 110]);
  } },
  game_table: { z: 9, draw: (p, W, H, seed) => {
    table([118, 70, 44], 5)(p, W, H, 0);
    p.floor(1.5, 1.5, W - 3, H - 3, [48, 40, 56], 5.05);
    const cols: RGB[] = [[214, 184, 132], [124, 122, 140], [70, 140, 196], [222, 86, 62], [250, 210, 90]];
    for (let i = 0; i < Math.floor((W - 4) / 2); i++) for (let j = 0; j < Math.floor((H - 4) / 2); j++) p.floor(2 + i * 2, 2 + j * 2, 1.7, 1.7, cols[(i * 3 + j * 5 + seed) % 5], 5.1);
    for (const [mx, my, c] of [[3, 3, [220, 60, 60]], [W - 4, H - 4, [60, 110, 220]], [W / 2, H / 2, [240, 200, 60]]] as [number, number, RGB][]) p.box(mx, my, 5.1, 0.9, 0.9, 2.2, c, { outline: false });
  }, light: { x: 9, y: 6, z: 12, color: [1.15, 0.88, 0.56], radius: 9, strength: 0.6 } },
  games_shelf: { z: 14, draw: shelf([90, 58, 40], 2, 11, gameBoxesIn(BOXC)) },
  observation_mirror: { z: 14, draw: (p, W) => {
    p.panelY(0.5, 0.4, 3, W - 1, 9, [70, 80, 100]);
    p.panelY(1, 0.38, 3.5, W - 2, 8, [150, 180, 210], [80, 100, 130]);
  } },
  rug_round: { z: 1, draw: (p, W, H) => {
    const [cx, cy] = p.P(W / 2, H / 2, 0);
    p.img.ellipse(cx - W * 1.8, cy - H * 0.9, cx + W * 1.8, cy + H * 0.9, [118, 36, 50]);
    p.img.ellipse(cx - W * 1.4, cy - H * 0.7, cx + W * 1.4, cy + H * 0.7, [140, 50, 62]);
  } },
  lamp_hanging: { z: 26, draw: (p, W, H) => {
    const cx = W / 2;
    const cy = H / 2;
    p.line([cx, cy, 26], [cx, cy, 20], [40, 30, 30]);
    p.box(cx - 1.5, cy - 1.5, 17.5, 3, 3, 2.5, [46, 112, 84], { glowLeft: WARM });
  }, light: { x: 6, y: 6, z: 16, color: [1.1, 0.86, 0.56], radius: 8, strength: 0.7 } },
  poster: { z: 14, draw: (p, W, _H, seed) => {
    const c: RGB = pickC([[40, 46, 86], [140, 46, 58], [46, 112, 84]], seed);
    p.panelY(0.8, 0.4, 5, W - 1.6, 7, c);
    p.panelY(1.6, 0.38, 8, W - 3.2, 2.5, PAPER);
  } },
  snack_table: { z: 9, draw: (p, W, H) => {
    table(WOOD, 4.5)(p, W, H, 0);
    p.box(1.2, 1.2, 4.5, 1.6, 1.6, 1.2, [230, 120, 70]);
    p.box(3, 2, 4.5, 1.2, 1.2, 2, [198, 64, 70]);
  } },
  clipboard_stand: { z: 12, draw: (p, W, H) => {
    p.line([W / 2, H / 2, 0], [W / 2, H / 2, 6], DARK);
    p.panelY(W / 2 - 1.2, H / 2, 6, 2.4, 3.2, [176, 128, 82]);
    p.panelY(W / 2 - 0.9, H / 2 + 0.02, 6.4, 1.8, 2.4, PAPER);
  } },
  easel: { z: 16, draw: (p, W, H, seed) => {
    const ex = 0.5;
    p.line([ex, H - 1, 0], [ex + 1, H - 2, 13], [120, 80, 50]);
    p.line([ex + 4, H - 1, 0], [ex + 3, H - 2, 13], [120, 80, 50]);
    p.box(ex - 0.3, H - 2.5, 5, 5, 0.6, 9, [246, 240, 226]);
    const art: RGB = pickC([[60, 64, 110], [250, 210, 200], [80, 140, 90]], seed);
    p.panelY(ex + 0.2, H - 1.88, 6, 4.4, 7, art);
  } },
  drafting_table: { z: 11, draw: (p, W, H) => {
    legs(p, W, H, 5, DARK);
    p.quad([[0.5, 0.5, 5], [W - 0.5, 0.5, 7.5], [W - 0.5, H - 0.5, 7.5], [0.5, H - 0.5, 5]], [236, 232, 220]);
    for (let k = 0; k < 4; k++) p.floor(2 + k * 2, 1.5, 1.6, 2, BOXC[k], 6 + k * 0.3);
  } },
  paint_shelf: { z: 14, draw: shelf([150, 110, 80], 2, 10, (p, x, z, i) => p.box(x, 1.6, z, 0.8, 0.8, 1.6, BOXC[i % 8], { outline: false })) },
  light_box: { z: 9, draw: (p, W, H) => {
    table(DARK, 4)(p, W, H, 0);
    p.floor(1, 1, W - 2, H - 2, [250, 250, 240], 4.05);
    p.quad([[1, 1, 4.06], [W - 1, 1, 4.06], [W - 1, H - 1, 4.06], [1, H - 1, 4.06]], [250, 250, 240], [240, 240, 220]);
  }, light: { x: 3, y: 3, z: 5, color: [0.95, 0.95, 1.0], radius: 4, strength: 0.35 } },
  stool: { z: 6, draw: (p, W, H) => {
    p.line([W / 2, H / 2, 0], [W / 2, H / 2, 3.5], DARK);
    p.box(W / 2 - 1.3, H / 2 - 1.3, 3.5, 2.6, 2.6, 0.8, [200, 150, 90]);
  } },
  canvas_stack: { z: 9, draw: (p, W) => {
    for (let k = 0; k < 3; k++) p.box(0.6 + k * 0.6, 1 + k * 0.7, 0, W - 2, 0.5, 6 - k, [246, 240, 226]);
  } },
  lamp_floor: { z: 18, draw: lamp(13, [214, 172, 92]), light: { x: 3, y: 3, z: 14, color: [1.0, 0.8, 0.52], radius: 7, strength: 0.55 } },
  print_rack: { z: 10, draw: printRack(BOXC) },
  pallet: { z: 12, draw: palletOf(BOXC) },
  shelf_rack: { z: 20, draw: shelfRack(BOXC) },
  forklift: { z: 14, draw: (p, W, H) => {
    p.box(2, 0.5, 0, W - 2.5, H - 1, 5, [240, 190, 50]);
    p.box(W - 4, 1, 5, 3, H - 2, 5, [60, 64, 74], { top: [80, 84, 94] });
    p.line([1, 1, 0], [1, 1, 12], [60, 60, 70]);
    p.box(0, 0.8, 1, 1.8, H - 1.6, 0.4, METAL);
  } },
  trolley: { z: 9, draw: trolleyOf(BOXC) },
  packing_table: { z: 10, draw: packingTable(BOXC) },
  tape_station: { z: 8, draw: (p, W, H) => {
    table(DARK, 4)(p, W, H, 0);
    p.box(1.5, 1.5, 4, 2, 2, 1.4, [198, 160, 90]);
  } },
  lamp_cage: { z: 14, draw: lamp(9, [80, 84, 96]), light: { x: 3, y: 3, z: 10, color: [0.9, 0.92, 1.0], radius: 6, strength: 0.45 } },
  box_stack: { z: 10, draw: stack([[198, 160, 110], [176, 140, 96], [214, 180, 130]], 3, 2.4) },
  sofa: { z: 9, draw: (p, W, H) => {
    p.box(0.5, 1.5, 0, W - 1, H - 2, 3.5, [176, 70, 70]);
    p.box(0.5, H - 1.5, 0, W - 1, 1, 7, [150, 56, 58]);
    p.box(0.5, 1.5, 0, 1.4, H - 2, 5.5, [150, 56, 58]);
    p.box(W - 1.9, 1.5, 0, 1.4, H - 2, 5.5, [150, 56, 58]);
  } },
  coffee_machine: { z: 14, draw: (p, W, H) => {
    p.box(0.8, 0.8, 0, W - 1.6, H - 1.6, 5, DARK, { top: [220, 210, 190] });
    p.box(1.2, 1.4, 5, W - 2.4, H - 2.4, 5, [190, 60, 60]);
    p.panelY(1.6, H - 0.95, 7.5, 1.2, 1, [120, 255, 140], [120, 255, 140]);
  } },
  fridge: { z: 18, draw: (p, W, H) => {
    p.box(0.6, 0.6, 0, W - 1.2, H - 1.2, 15, [220, 226, 232]);
    p.line([W - 1.6, H - 0.59, 6], [W - 1.6, H - 0.59, 12], [120, 126, 140]);
  } },
  arcade: { z: 18, draw: (p, W, H) => {
    p.box(0.8, 0.8, 0, W - 1.6, H - 1.6, 15, [60, 40, 120]);
    p.panelY(1.2, H - 0.79, 8, W - 2.4, 4, [80, 220, 255], [80, 220, 255]);
    p.box(1, H - 2.4, 5.5, W - 2, 1.6, 1, [240, 190, 50]);
  }, light: { x: 3, y: 4, z: 10, color: [0.6, 0.9, 1.1], radius: 5, strength: 0.4 } },
  coffee_table: { z: 6, draw: (p, W, H) => {
    table([120, 80, 50], 3)(p, W, H, 0);
    p.box(1.5, 1.5, 3, 1.2, 1.2, 1.2, PAPER, { outline: false });
  } },
  plant_big: { z: 20, draw: plantPot(7, POT, 3) },
  beanbag: { z: 6, draw: (p, W, H, seed) => {
    const [cx, cy] = p.P(W / 2, H / 2, 0);
    const c = BOXC[seed % 7];
    p.img.ellipse(cx - 6, cy - 7, cx + 6, cy + 1, c);
    p.img.ellipse(cx - 4, cy - 7, cx + 2, cy - 3, shade(c, 1.2));
  } },
  water_cooler: { z: 16, draw: (p, W, H) => {
    p.box(1, 1, 0, W - 2, H - 2, 8, [220, 226, 232]);
    p.box(1.4, 1.4, 8, W - 2.8, H - 2.8, 5, [140, 200, 240], { top: [170, 220, 250] });
  } },
  sculpt_bench: { z: 11, draw: (p, W, H) => {
    table([150, 110, 80], 5)(p, W, H, 0);
    for (let k = 0; k < 3; k++) p.box(1.5 + k * 3, 1.5, 5, 1, 1, 2 + k, pickC([[150, 156, 170], [196, 80, 80], [80, 140, 200]], k));
  } },
  kiln: { z: 14, draw: machine([130, 90, 70], 10, [255, 140, 60]), light: { x: 3, y: 4, z: 6, color: [1.2, 0.6, 0.3], radius: 5, strength: 0.45 } },
  resin_printer: { z: 12, draw: machine([60, 64, 74], 8, [255, 160, 60]), light: { x: 3, y: 4, z: 5, color: [1.1, 0.7, 0.4], radius: 4, strength: 0.3 } },
  mini_shelf: { z: 14, draw: shelf([90, 58, 40], 2, 10, minisIn(BOXC)) },
  press: { z: 16, draw: (p, W, H) => {
    p.box(0.5, 0.5, 0, W - 1, H - 1, 12, [78, 86, 102]);
    p.box(0.5, H - 0.51, 5, W - 1, 0.01, 2, [210, 70, 70], { outline: false });
    for (let k = 0; k < 3; k++) p.box(2 + k * 4.5, 1.5, 12, 3.5, H - 3, 1.2, [150, 156, 170], { outline: false });
    p.panelY(1.5, H - 0.48, 2, 2, 1.5, [120, 255, 140], [120, 255, 140]);
  } },
  cutter: { z: 10, draw: machine([90, 96, 110], 7, [255, 220, 120]) },
  conveyor: { z: 6, draw: conveyorOf(BOXC) },
  paper_stack: { z: 6, draw: stack([PAPER, [236, 232, 220]], 2, 1.8) },
  phone_desk: { z: 10, draw: (p, W, H) => {
    table(WOOD)(p, W, H, 0);
    p.box(2, 1.5, 5, 2, 1.4, 0.8, [190, 60, 60]);
    p.panelY(5, 1.2, 5, 2.4, 2.4, [214, 170, 92]);
  } },
  display_stand: { z: 12, draw: displayStand(BOXC), light: { x: 3, y: 3, z: 8, color: [1, 0.95, 0.85], radius: 3, strength: 0.25 } },
  counter: { z: 10, draw: (p, W, H) => {
    p.box(0.3, 0.3, 0, W - 0.6, H - 0.6, 6, [92, 156, 140], { top: [214, 200, 170] });
    p.box(W - 3, 1, 6, 2, 1.4, 1.6, [60, 64, 74]);
  } },
  till: { z: 9, draw: (p, W, H) => {
    p.box(1, 1, 0, W - 2, H - 2, 5, DARK);
    p.box(1.5, 1.5, 5, W - 3, H - 3, 2, [60, 64, 74]);
    p.panelY(1.8, H - 1.49, 6, 1.6, 0.8, [120, 255, 140], [120, 255, 140]);
  } },
  sign: { z: 16, draw: (p, W) => {
    p.panelY(0.5, 0.4, 8, W - 1, 5, [232, 88, 72], [232, 88, 72]);
    p.panelY(1, 0.38, 9.5, W - 2, 2, PAPER);
  }, light: { x: 3, y: 1, z: 10, color: [1.1, 0.6, 0.5], radius: 4, strength: 0.3 } },
  display_case: { z: 12, draw: displayCase(BOXC) },
  trophy_shelf: { z: 16, draw: shelf(DARK, 2, 11, (p, x, z) => {
    p.box(x, 1.6, z, 0.6, 0.6, 0.6, [160, 120, 50], { outline: false });
    p.box(x - 0.1, 1.5, z + 0.6, 0.8, 0.8, 1.6, [236, 190, 80], { outline: false });
  }) },
  bench: { z: 7, draw: (p, W, H) => {
    legs(p, W, H, 3, DARK);
    p.box(0.5, 1, 3, W - 1, H - 2, 0.8, [176, 124, 80]);
  } },
  planter: { z: 14, draw: (p, W, H, seed) => {
    p.box(0.4, 0.4, 0, W - 0.8, H - 0.8, 3, [150, 100, 70]);
    p.floor(0.8, 0.8, W - 1.6, H - 1.6, [96, 66, 44], 3.05);
    for (let k = 0; k < Math.max(1, Math.floor(W / 4)); k++) {
      const leaf = leafy(4, seed + k * 13, pickC([[62, 132, 80], [210, 90, 120], [240, 200, 80]], seed + k));
      const [sx, sy] = p.P(2 + k * 4, H / 2, 3);
      p.img.blit(leaf, sx - (leaf.w >> 1), sy - leaf.h + 3);
    }
  } },
  fountain: { z: 14, draw: (p, W, H) => {
    p.box(0.5, 0.5, 0, W - 1, H - 1, 2.5, [200, 200, 206]);
    p.floor(1.3, 1.3, W - 2.6, H - 2.6, [90, 170, 220], 2.55);
    p.box(W / 2 - 0.8, H / 2 - 0.8, 2.5, 1.6, 1.6, 6, [200, 200, 206]);
    p.box(W / 2 - 1.6, H / 2 - 1.6, 8.5, 3.2, 3.2, 0.8, [200, 200, 206]);
  } },
  tree: { z: 26, draw: (p, W, H, seed) => {
    p.box(W / 2 - 1.5, H / 2 - 1.5, 0, 3, 3, 2.5, POT);
    p.line([W / 2, H / 2, 2.5], [W / 2, H / 2, 10], [110, 70, 44]);
    const leaf = leafy(8, seed, [52, 120, 70]);
    const [sx, sy] = p.P(W / 2, H / 2, 10);
    p.img.blit(leaf, sx - (leaf.w >> 1), sy - leaf.h + 6);
  } },
  lamp_string: { z: 18, draw: (p, W) => {
    for (const x of [0.5, W - 0.5]) p.line([x, 0.5, 0], [x, 0.5, 14], DARK);
    for (let k = 0; k < 6; k++) {
      const x = 0.8 + ((W - 1.6) * k) / 5;
      const z = 13 - Math.sin((k / 5) * Math.PI) * 2;
      p.quad([[x, 0.5, z], [x + 0.6, 0.5, z], [x + 0.6, 0.5, z - 0.8], [x, 0.5, z - 0.8]], WARM, WARM);
    }
  }, light: { x: 6, y: 1, z: 12, color: [1.1, 0.9, 0.6], radius: 6, strength: 0.35 } },
  pergola: { z: 20, draw: (p, W, H) => {
    for (const [x, y] of [[0.5, 0.5], [W - 1, 0.5], [0.5, H - 1], [W - 1, H - 1]]) p.box(x, y, 0, 0.6, 0.6, 15, [200, 170, 130]);
    for (let k = 0; k < 5; k++) p.box(0.3 + (k * (W - 0.6)) / 4, 0.3, 15, 0.5, H - 0.6, 0.8, [176, 140, 100]);
  } },
  bird_bath: { z: 8, draw: (p, W, H) => {
    p.box(W / 2 - 0.6, H / 2 - 0.6, 0, 1.2, 1.2, 4, [200, 200, 206]);
    p.box(W / 2 - 2, H / 2 - 2, 4, 4, 4, 1, [200, 200, 206], { top: [120, 190, 230] });
  } },
  pet_bed: { z: 3, draw: (p, W, H) => {
    const [cx, cy] = p.P(W / 2, H / 2, 0);
    p.img.ellipse(cx - 5, cy - 3, cx + 5, cy + 2, [150, 90, 160]);
    p.img.ellipse(cx - 3, cy - 2, cx + 3, cy + 1, [190, 140, 200]);
  } },
  cat_tree: { z: 18, draw: (p, W, H) => {
    p.box(0.8, 0.8, 0, W - 1.6, H - 1.6, 1, [190, 160, 120]);
    p.box(W / 2 - 0.6, H / 2 - 0.6, 1, 1.2, 1.2, 12, [214, 190, 150]);
    p.box(0.8, 0.8, 7, W - 1.6, H - 1.6, 1, [150, 90, 160]);
    p.box(1.2, 1.2, 13, W - 2.4, H - 2.4, 1.2, [150, 90, 160]);
  } },
};

// ---------------------------------------------------------------------------------------------------------------
// Era looks: the same piece of furniture as it looked in each decade band, so a 1965 garage and a 2035 tower read
// differently at a glance (typewriters -> beige boxes -> towers -> flat screens; rotary phones -> smartphones).
// A piece without an era look (or a call without a year) draws its timeless default above.

/** Decade bands for furniture looks: 0 = 1965–79, 1 = 1980–94, 2 = 1995–2009, 3 = 2010 on. */
export type FurnitureEra = 0 | 1 | 2 | 3;
/** One representative year per era band (handy for previews and tests). */
export const FURNITURE_ERA_YEARS: readonly number[] = [1970, 1987, 2002, 2020];
export function furnitureEra(year: number): FurnitureEra {
  return year < 1980 ? 0 : year < 1995 ? 1 : year < 2010 ? 2 : 3;
}

type Light = NonNullable<Spec["light"]>;
/** An era's draw, optionally with its own light (null = this era's version gives off no light). */
interface EraLook {
  draw: Draw;
  light?: Light | null;
}

/** Game-box colours by era: muted 60s/70s, loud 80s, the glossy default for the 90s/00s, varied premium 2010s. */
const ERA_BOXES: Record<FurnitureEra, RGB[]> = {
  0: [[176, 92, 56], [92, 112, 104], [204, 160, 72], [118, 128, 76], [140, 96, 70], [196, 120, 60], [84, 120, 128], [222, 206, 170]],
  1: [[236, 64, 140], [40, 110, 236], [250, 216, 40], [52, 196, 90], [146, 64, 214], [250, 120, 30], [40, 200, 220], [230, 44, 56]],
  2: BOXC,
  3: [[40, 44, 58], [236, 232, 222], [28, 104, 118], [184, 64, 54], [222, 172, 64], [96, 66, 124], [84, 138, 98], [214, 124, 100]],
};

const TEAK: RGB = [150, 98, 60];
const BRASS: RGB = [200, 160, 76];
const BEIGE: RGB = [216, 206, 178];
const PCGREY: RGB = [196, 196, 190];
const SLATE: RGB = [36, 38, 46];
const CHROME: RGB = [190, 196, 204];
const LAMINATE: RGB = [228, 224, 216];
const COOL: RGB = [236, 244, 255];
const PHOSPHOR: RGB = [80, 190, 120];
const DESKTOP: RGB = [70, 124, 204];

const screenLight = (color: RGB, x = 5, strength = 0.25): Light => ({ x, y: 2, z: 7, color, radius: 6, strength });

/** A slim flat screen on a foot, facing the sitter (+y). */
const flatScreen = (p: IsoPainter, x: number, y: number, w: number, h: number, z = 5) => {
  p.box(x + w / 2 - 0.6, y + 0.2, z, 1.2, 0.8, 0.3, [70, 72, 82], { outline: false });
  p.line([x + w / 2, y + 0.4, z + 0.3], [x + w / 2, y + 0.4, z + 1.2], SLATE);
  p.box(x, y, z + 1.2, w, 0.4, h, SLATE);
  p.panelY(x + 0.3, y + 0.41, z + 1.5, w - 0.6, h - 0.6, SCREEN, SCREEN);
};
/** An open laptop: silver base and a lid tipped back from the hinge. */
const LAPTOP: RGB = [98, 102, 114];
const laptop = (p: IsoPainter, x: number, y: number, z = 5) => {
  p.box(x, y, z, 2.8, 1.8, 0.3, LAPTOP);
  p.quad([[x, y - 0.4, z + 2.6], [x + 2.8, y - 0.4, z + 2.6], [x + 2.8, y, z + 0.3], [x, y, z + 0.3]], shade(LAPTOP, 0.7));
  p.quad([[x + 0.3, y - 0.36, z + 2.3], [x + 2.5, y - 0.36, z + 2.3], [x + 2.5, y - 0.04, z + 0.6], [x + 0.3, y - 0.04, z + 0.6]], SCREEN, SCREEN);
};
const keyboard = (p: IsoPainter, x: number, y: number, w: number, c: RGB) => p.box(x, y, 5, w, 1.2, 0.4, c, { outline: false });
const mug = (p: IsoPainter, x: number, y: number, c: RGB = [240, 238, 230]) => p.box(x, y, 5, 0.8, 0.8, 1.1, c, { outline: false });

/** A beige desktop case with a CRT on top. */
const beigeComputer = (p: IsoPainter, x: number) => {
  p.box(x, 0.8, 5, 5.2, 2.8, 1.2, BEIGE);
  p.panelY(x + 0.4, 3.61, 5.4, 1.6, 0.5, [70, 70, 74]);
  p.box(x + 0.6, 1, 6.2, 4, 2.4, 3.4, BEIGE);
  p.panelY(x + 1, 3.41, 6.7, 3.2, 2.5, PHOSPHOR, PHOSPHOR);
  p.line([x + 1.4, 3.42, 8.6], [x + 3, 3.42, 8.6], [200, 255, 210]);
  keyboard(p, x + 0.4, 4.2, 4.4, BEIGE);
};
/** A grey tower beside a chunky monitor (flat=true: an early thin-ish flat panel instead of a CRT). */
const towerPc = (p: IsoPainter, flat: boolean) => {
  p.box(8.6, 0.8, 5, 1.8, 3, 4.8, PCGREY);
  p.panelY(8.9, 3.81, 8.4, 1.2, 0.5, [80, 80, 86]);
  p.panelY(9.2, 3.81, 6, 0.6, 0.6, [120, 255, 140], [120, 255, 140]);
  if (flat) {
    p.box(3.8, 1.2, 5, 1.6, 1.2, 0.8, PCGREY);
    p.box(2.4, 1.4, 5.8, 4.8, 0.9, 3.8, PCGREY);
    p.panelY(2.8, 2.31, 6.2, 4, 3, DESKTOP, DESKTOP);
  } else {
    p.box(2.6, 0.8, 5, 4.6, 2.6, 4, PCGREY);
    p.panelY(3, 3.41, 5.6, 3.8, 3, DESKTOP, DESKTOP);
  }
  keyboard(p, 2.6, 4.2, 4.6, PCGREY);
  p.box(7.6, 4.3, 5, 0.6, 0.8, 0.4, PCGREY, { outline: false });
};

const typewriterDesk: Draw = (p, W, H) => {
  table(TEAK)(p, W, H, 0);
  // the typewriter: body, platen roller, a sheet rolled in, the key rows
  p.box(3.6, 1.8, 5, 3.6, 2.4, 1.2, [88, 132, 116]);
  p.box(3.4, 1.4, 6.2, 4, 0.8, 0.8, [40, 44, 50]);
  p.panelY(4.4, 1.8, 7, 2, 2.6, PAPER);
  p.floor(4, 3.4, 2.8, 0.6, [214, 210, 196], 6.25);
  // paper stacks and manila folders
  p.box(0.8, 1.2, 5, 2.2, 2.6, 1.8, PAPER);
  p.line([0.8, 3.8, 5.9], [3, 3.8, 5.9], [200, 196, 184]);
  p.box(8.4, 1.4, 5, 2.4, 2.2, 0.8, [214, 180, 110]);
  p.box(8.6, 1.5, 5.8, 2, 1.9, 0.9, PAPER);
};

const shelfFrame: Record<FurnitureEra, RGB> = { 0: TEAK, 1: DARK, 2: [196, 156, 112], 3: LAMINATE };

/** Game boxes on a shelf, shaped by era: long flat 60s/70s boxes lie in stacks; 2010s mixes big squares and small boxes. */
const eraGameBoxes = (e: FurnitureEra): Fill => {
  const pal = ERA_BOXES[e];
  if (e === 0)
    return (p, x, z, i) => {
      if (Math.round((x - 0.6) / 1.1) % 2) return;
      for (let j = 0; j < 3; j++) p.box(x, 1.1, z + j * 0.9, 2, 1.6, 0.8, pal[(i + j * 3) % 8], { outline: j === 2 });
    };
  if (e === 3)
    return (p, x, z, i) => {
      const c = pal[(i * 3) % 8];
      const kind = (i * 7) % 5;
      const [w, h] = kind === 0 ? [1, 4.4] : kind === 1 ? [0.9, 2.6] : kind === 2 ? [1, 3.6] : kind === 3 ? [0.8, 2] : [1, 4];
      p.panelY(x, 2.82, z, w, h, c);
      p.panelY(x, 2.83, z + h - 0.9, w, 0.5, shade(c, c[0] + c[1] + c[2] > 500 ? 0.75 : 1.45));
    };
  return (p, x, z, i) => p.panelY(x, 2.82, z, 0.95, e === 2 && i % 3 === 1 ? 3.2 : 4, pal[(i * 3) % 8]);
};

const eraBooks = (e: FurnitureEra): Fill => {
  const pal = ERA_BOXES[e];
  return (p, x, z, i) => p.panelY(x, 2.82, z, 0.8, e === 1 ? 3.6 : 2.8 + ((i * 5) % 4) * 0.3, pal[i % 8]);
};

const eraLampDesk = (e: FurnitureEra): EraLook => {
  if (e === 0)
    return { draw: (p, W, H) => {
      // brass banker's lamp with a long green glass shade
      const cx = W / 2;
      const cy = H / 2;
      p.box(cx - 1.2, cy - 0.8, 0, 2.4, 1.6, 0.6, BRASS);
      p.line([cx, cy, 0.6], [cx, cy, 4.4], shade(BRASS, 0.8));
      p.box(cx - 2, cy - 0.7, 4.4, 4, 1.4, 1.2, [46, 112, 84], { glowLeft: WARM, top: [70, 150, 110] });
      p.quad([[cx - 1.6, cy - 0.4, 4.4], [cx + 1.6, cy - 0.4, 4.4], [cx + 1.6, cy + 0.4, 4.4], [cx - 1.6, cy + 0.4, 4.4]], WARM, WARM);
    } };
  if (e === 3)
    return {
      draw: (p, W, H) => {
        // LED bar: slim foot, one stem, a long thin head with a cool strip of light underneath
        const cx = W / 2;
        const cy = H / 2;
        p.box(cx - 1.4, cy - 0.7, 0, 2.8, 1.4, 0.4, LAMINATE);
        p.line([cx - 1, cy, 0.4], [cx - 1, cy, 6.4], [170, 174, 182]);
        p.box(cx - 2.6, cy - 0.3, 6.4, 5.2, 0.7, 0.5, LAMINATE);
        p.quad([[cx - 2.4, cy + 0.4, 6.4], [cx + 2.4, cy + 0.4, 6.4], [cx + 2.4, cy + 0.38, 6], [cx - 2.4, cy + 0.38, 6]], COOL, COOL);
      },
      light: { x: 3, y: 3, z: 6, color: [0.9, 0.95, 1.08], radius: 5, strength: 0.5 },
    };
  const c: RGB = e === 1 ? [204, 64, 54] : [56, 58, 66];
  return { draw: (p, W, H) => {
    // angled lamp: a jointed arm and a cone head pointing at the desk
    const cx = W / 2;
    const cy = H / 2;
    p.box(cx - 1, cy - 1, 0, 2, 2, 0.5, c);
    p.line([cx, cy, 0.5], [cx - 1.4, cy - 1.4, 4.6], shade(c, 0.8));
    p.line([cx - 1.4, cy - 1.4, 4.6], [cx + 1, cy + 0.2, 6.4], shade(c, 0.8));
    p.box(cx + 0.4, cy - 0.4, 4.8, 1.8, 1.8, 1.4, c, { glowLeft: WARM });
    p.quad([[cx + 0.6, cy - 0.2, 4.8], [cx + 2, cy - 0.2, 4.8], [cx + 2, cy + 1.2, 4.8], [cx + 0.6, cy + 1.2, 4.8]], WARM, WARM);
  } };
};

const eraLampFloor = (e: FurnitureEra): EraLook => {
  if (e === 0) return { draw: lamp(12, [214, 120, 52]), light: { x: 3, y: 3, z: 13, color: [1.05, 0.76, 0.46], radius: 7, strength: 0.55 } };
  if (e === 3)
    return {
      draw: (p, W, H) => {
        // a slim LED column on a round foot
        const cx = W / 2;
        const cy = H / 2;
        p.box(cx - 1.2, cy - 1.2, 0, 2.4, 2.4, 0.4, SLATE);
        p.box(cx - 0.45, cy - 0.45, 0.4, 0.9, 0.9, 15, [226, 232, 242], { glowLeft: COOL });
      },
      light: { x: 3, y: 3, z: 10, color: [0.92, 0.96, 1.08], radius: 7, strength: 0.5 },
    };
  const c: RGB = e === 1 ? [52, 52, 60] : [184, 188, 196];
  return { draw: (p, W, H) => {
    // torchiere: a bowl at the top throwing light at the ceiling
    const cx = W / 2;
    const cy = H / 2;
    p.box(cx - 1.2, cy - 1.2, 0, 2.4, 2.4, 0.5, c);
    p.line([cx, cy, 0.5], [cx, cy, 13], shade(c, 0.85));
    p.box(cx - 0.7, cy - 0.7, 12.4, 1.4, 1.4, 0.8, c, { outline: false });
    p.box(cx - 1.8, cy - 1.8, 13.2, 3.6, 3.6, 1.2, c, { top: WARM });
    p.quad([[cx - 1.4, cy - 1.4, 14.4], [cx + 1.4, cy - 1.4, 14.4], [cx + 1.4, cy + 1.4, 14.4], [cx - 1.4, cy + 1.4, 14.4]], WARM, WARM);
  }, light: { x: 3, y: 3, z: 15, color: [1.0, 0.84, 0.58], radius: 8, strength: 0.5 } };
};

const eraChair = (e: FurnitureEra): Draw => {
  if (e === 0)
    return (p, W, H) => {
      // wooden chair: four legs, a slatted back
      for (const [x, y] of [[1.2, H - 1.2], [W - 1.2, H - 1.2], [W - 1.2, 1.2], [1.2, 1.2]]) p.line([x, y, 0], [x, y, 3], shade(WOOD, 0.6));
      p.box(1, 1, 3, W - 2, H - 2, 0.6, WOOD);
      for (const x of [1.3, W - 1.3]) p.line([x, 1.3, 3.6], [x, 1.3, 7.2], shade(WOOD, 0.75));
      p.box(1, 1, 6.4, W - 2, 0.6, 1.2, WOOD);
      p.line([1.3, 1.3, 5], [W - 1.3, 1.3, 5], shade(WOOD, 0.75));
    };
  const seat: RGB = e === 1 ? [184, 112, 60] : e === 2 ? [60, 68, 104] : SLATE;
  return (p, W, H) => {
    // swivel chair on a five-spoke base
    const cx = W / 2;
    const cy = H / 2;
    for (const [dx, dy] of [[-2, 0], [2, 0], [0, -2], [0, 2], [1.4, 1.4]]) p.line([cx, cy, 0.6], [cx + dx, cy + dy, 0], [60, 62, 70]);
    p.line([cx, cy, 0.6], [cx, cy, 3], [80, 84, 92]);
    p.box(1, 1, 3, W - 2, H - 2, 0.8, seat);
    if (e === 3) {
      // mesh back: dark frame, see-through weave, a headrest
      p.box(1, 1, 3.8, W - 2, 0.6, 4.6, SLATE);
      p.panelY(1.5, 1.61, 4.3, W - 3, 3.6, [96, 100, 112]);
      for (let k = 0; k < 3; k++) p.line([1.6, 1.62, 5 + k * 1.1], [W - 1.6, 1.62, 5 + k * 1.1], [70, 74, 84]);
      p.box(1.8, 1, 8.6, W - 3.6, 0.6, 1, SLATE);
    } else {
      p.box(1.4, 1, 4.4, W - 2.8, 0.8, 3.4, seat);
      if (e === 2) for (const x of [0.8, W - 1.4]) p.box(x, 1.6, 3.8, 0.6, H - 3, 1.4, [44, 46, 54], { outline: false });
    }
  };
};

const eraPoster = (e: FurnitureEra): Draw | undefined => {
  if (e === 0)
    return (p, W, _H, seed) => {
      const bg = pickC([[206, 160, 64], [176, 92, 56], [118, 128, 76]], seed);
      p.panelY(0.8, 0.4, 5, W - 1.6, 7, bg);
      p.panelY(1.6, 0.39, 6, W - 3.2, 3.6, pickC([[176, 92, 56], [222, 206, 170], [204, 160, 72]], seed + 1));
      p.panelY(2.2, 0.38, 7, W - 4.4, 1.6, shade(bg, 0.7));
      p.panelY(1.6, 0.38, 10.4, W - 3.2, 1, [240, 228, 200]);
    };
  if (e === 1)
    return (p, W, _H, seed) => {
      // neon on black: a sunset stripe, a grid and a bright title band
      p.panelY(0.8, 0.4, 5, W - 1.6, 7, [28, 20, 58]);
      p.panelY(1.4, 0.39, 7.6, W - 2.8, 1.4, pickC([[236, 64, 140], [40, 200, 220], [250, 120, 30]], seed));
      for (let k = 0; k < 2; k++) p.line([1.2, 0.38, 5.8 + k * 0.9], [W - 1.2, 0.38, 5.8 + k * 0.9], [146, 64, 214]);
      p.panelY(1.4, 0.38, 10, W - 2.8, 1.4, [250, 216, 40]);
    };
  if (e === 3)
    return (p, W, _H, seed) => {
      // minimal print: off-white, one bold block, a thin rule
      p.panelY(0.8, 0.4, 5, W - 1.6, 7, [236, 232, 224]);
      p.panelY(1.6, 0.39, 7, W - 3.2, 3.4, pickC([ERA_BOXES[3][2], ERA_BOXES[3][3], ERA_BOXES[3][4]], seed));
      p.line([1.6, 0.38, 6], [W - 2.4, 0.38, 6], SLATE);
    };
  return undefined;
};

const coffeeCabinet = (p: IsoPainter, W: number, H: number, c: RGB = DARK) => p.box(0.8, 0.8, 0, W - 1.6, H - 1.6, 5, c, { top: [220, 210, 190] });
const eraCoffee = (e: FurnitureEra): Draw => {
  if (e === 0)
    return (p, W, H) => {
      // a chrome percolator and two cups on a teak cabinet
      coffeeCabinet(p, W, H, TEAK);
      p.box(1.4, 1.4, 5, 2, 2, 3.6, CHROME, { top: [220, 224, 230] });
      p.box(2, 2, 8.6, 0.8, 0.8, 0.6, SLATE, { outline: false });
      for (const y of [1.2, 3.2]) p.box(W - 2.2, y, 5, 0.9, 0.9, 0.9, PAPER, { outline: false });
    };
  if (e === 3)
    return (p, W, H) => {
      // espresso machine: wide chrome body, dark top, group head, a small lit display
      coffeeCabinet(p, W, H, [70, 72, 80]);
      p.box(1, 1.2, 5, W - 2, H - 2.6, 4.4, CHROME, { top: [60, 62, 70] });
      p.box(2, H - 1.5, 6.4, 1, 0.8, 0.6, SLATE, { outline: false });
      p.panelY(W - 2.8, H - 1.39, 8, 1.2, 0.8, [120, 200, 255], [120, 200, 255]);
      p.box(2.2, H - 1.4, 5, 0.7, 0.7, 0.8, PAPER, { outline: false });
    };
  const body: RGB = e === 1 ? BEIGE : [52, 54, 60];
  const led: RGB = e === 1 ? [255, 90, 60] : [120, 255, 140];
  return (p, W, H) => {
    // drip machine with a glass pot of coffee
    coffeeCabinet(p, W, H);
    p.box(1.2, 1.2, 5, W - 2.4, 1.6, 5, body);
    p.box(1.2, 1.2, 9, W - 2.4, H - 2.6, 1, body);
    p.box(1.8, 2.8, 5, 2.2, 1.6, 2.2, [104, 62, 40], { top: [140, 96, 70] });
    p.panelY(W - 2, H - 1.79, 6, 0.6, 0.6, led, led);
  };
};

const eraCooler = (e: FurnitureEra): Draw | undefined => {
  if (e === 0)
    return (p, W, H) => {
      // stoneware crock on a metal stand
      p.box(1.2, 1.2, 0, W - 2.4, H - 2.4, 6.5, [150, 156, 170]);
      p.box(1.2, 1.2, 6.5, W - 2.4, H - 2.4, 5, [224, 214, 188]);
      p.panelY(1.2, H - 1.19, 8.2, W - 2.4, 0.8, [70, 110, 170]);
      p.box(W / 2 - 0.3, H - 1.2, 7, 0.6, 0.6, 0.6, CHROME, { outline: false });
    };
  if (e === 3)
    return (p, W, H) => {
      // slim plumbed-in dispenser with a recessed tap and a blue status light
      p.box(1.3, 1.3, 0, W - 2.6, H - 2.6, 12, [232, 234, 238]);
      p.panelY(1.8, H - 1.29, 5.5, W - 3.6, 3, [52, 56, 66]);
      p.panelY(2.4, H - 1.28, 10, 1, 0.6, [90, 180, 255], [90, 180, 255]);
    };
  return undefined;
};

const eraWhiteboard = (e: FurnitureEra): Draw | undefined => {
  if (e === 0)
    return (p, W) => {
      // chalkboard in a wooden frame, with chalk scribbles
      p.panelY(0.5, 0.4, 4, W - 1, 8, [130, 88, 56]);
      p.panelY(1, 0.39, 4.6, W - 2, 6.8, [52, 84, 66]);
      p.line([1.6, 0.38, 10], [W - 3, 0.38, 9.4], [220, 226, 214]);
      p.line([1.6, 0.38, 8.4], [W / 2, 0.38, 8], [220, 226, 214]);
      p.line([W / 2 + 1, 0.38, 7], [W - 2, 0.38, 6.2], [240, 230, 170]);
    };
  if (e === 3)
    return (p, W) => {
      // frosted glass board covered in sticky notes
      p.panelY(0.5, 0.4, 4, W - 1, 8, [206, 226, 230]);
      const notes: RGB[] = [[250, 222, 120], [244, 180, 170], [160, 210, 236], [180, 226, 160]];
      for (let r = 0; r < 2; r++) for (let k = 0; k < 4; k++) p.panelY(1.4 + k * 2.4, 0.38, 6 + r * 2.6, 1.5, 1.5, notes[(k + r * 3) % 4]);
    };
  return undefined;
};

const eraPhoneDesk = (e: FurnitureEra): EraLook => {
  if (e === 0)
    return { draw: (p, W, H) => {
      // rotary phone, a card file and a notepad
      table(TEAK)(p, W, H, 0);
      p.box(1.8, 1.6, 5, 2.4, 1.8, 1, [44, 44, 50]);
      p.box(1.6, 1.4, 6, 2.8, 0.8, 0.6, [44, 44, 50]);
      p.floor(2.5, 2.4, 1.1, 0.9, [214, 214, 206], 6.02);
      p.box(6, 1.4, 5, 1.8, 1.2, 0.7, [150, 150, 154]);
      p.panelY(6.2, 2, 5.7, 1.4, 1.2, PAPER);
      p.floor(8.6, 1.6, 2, 2.4, [250, 222, 120], 5.05);
    }, light: null };
  if (e === 1)
    return { draw: (p, W, H) => {
      // push-button phone and an answering machine with its red light on
      table(WOOD)(p, W, H, 0);
      p.box(1.8, 1.6, 5, 2.6, 2, 0.9, BEIGE);
      p.box(1.6, 1.4, 5.9, 3, 0.8, 0.6, BEIGE);
      p.floor(2.4, 2.4, 1.6, 1, [140, 136, 124], 5.92);
      p.box(5.8, 1.4, 5, 2.4, 1.8, 0.8, [60, 60, 66]);
      p.panelY(6.2, 3.21, 5.2, 0.6, 0.4, [255, 70, 60], [255, 70, 60]);
      p.floor(8.8, 1.6, 2, 2.4, [250, 222, 120], 5.05);
    }, light: null };
  if (e === 2)
    return { draw: (p, W, H) => {
      // cordless handset in its cradle and a fax machine feeding paper
      table([196, 160, 116])(p, W, H, 0);
      p.box(1.8, 2, 5, 1.8, 1.6, 0.5, [70, 74, 84]);
      p.box(2.3, 2.2, 5.5, 0.8, 0.6, 2.6, [70, 74, 84]);
      p.panelY(2.4, 2.81, 7, 0.6, 0.5, [140, 220, 160], [140, 220, 160]);
      p.box(5.6, 1.2, 5, 3.4, 2.6, 1.4, PCGREY);
      p.panelY(6.2, 1.4, 6.4, 2.2, 1.8, PAPER);
      p.floor(9.6, 1.6, 1.6, 2.2, [250, 222, 120], 5.05);
    }, light: screenLight([0.7, 0.95, 0.8], 3, 0.15) };
  return { draw: (p, W, H) => {
    // a smartphone on a stand, a laptop and a coffee
    table(LAMINATE)(p, W, H, 0);
    p.box(2, 2, 5, 1.2, 1, 0.4, SLATE);
    p.panelY(2.1, 2.4, 5.4, 1, 2, SLATE);
    p.panelY(2.2, 2.42, 5.6, 0.8, 1.6, SCREEN, SCREEN);
    laptop(p, 5.6, 2.6);
    mug(p, 9.6, 1.4, [222, 124, 100]);
  }, light: screenLight([0.7, 0.9, 1.05], 6, 0.22) };
};

const eraTill = (e: FurnitureEra): Draw | undefined => {
  if (e === 0)
    return (p, W, H) => {
      // brass cash register with a pop-up price display
      p.box(1, 1, 0, W - 2, H - 2, 4.4, DARK);
      p.box(1.3, 1.3, 4.4, W - 2.6, H - 2.6, 2.2, BRASS);
      p.box(1.8, 1.4, 6.6, 2.4, 1, 1.4, shade(BRASS, 0.9));
      p.panelY(2, 2.41, 6.9, 2, 0.8, PAPER);
      p.floor(1.8, H - 2.4, W - 3.6, 0.8, [230, 220, 190], 6.62);
    };
  if (e === 3)
    return (p, W, H) => {
      // a tablet on a swivel stand and a card reader
      p.box(1, 1, 0, W - 2, H - 2, 5, LAMINATE);
      p.line([W / 2, H / 2, 5], [W / 2, H / 2, 6.4], SLATE);
      p.quad([[1.4, 2.2, 8.6], [W - 1.4, 2.2, 8.6], [W - 1.4, 2.8, 6.2], [1.4, 2.8, 6.2]], SLATE);
      p.quad([[1.7, 2.24, 8.3], [W - 1.7, 2.24, 8.3], [W - 1.7, 2.76, 6.5], [1.7, 2.76, 6.5]], SCREEN, SCREEN);
      p.box(W - 2.2, H - 2, 5, 0.8, 1, 0.5, SLATE, { outline: false });
    };
  return undefined;
};

const eraSign = (e: FurnitureEra): EraLook | undefined => {
  if (e === 0)
    return { draw: (p, W) => {
      // hand-painted wooden sign
      p.panelY(0.5, 0.4, 8, W - 1, 5, [150, 100, 60]);
      p.panelY(1, 0.38, 8.6, W - 2, 3.8, [236, 222, 190]);
      p.panelY(1.4, 0.37, 9.8, W - 2.8, 1.4, [176, 70, 52]);
    }, light: null };
  if (e === 3)
    return { draw: (p, W) => {
      // thin-bezel LED screen
      p.panelY(0.5, 0.4, 8, W - 1, 5, SLATE);
      p.panelY(0.8, 0.39, 8.3, W - 1.6, 4.4, [60, 170, 230], [60, 170, 230]);
      p.panelY(1.2, 0.38, 9.8, W - 2.4, 1.6, PAPER, PAPER);
    }, light: { x: 3, y: 1, z: 10, color: [0.6, 0.85, 1.1], radius: 4, strength: 0.3 } };
  return undefined;
};

const eraLampHanging = (e: FurnitureEra): EraLook | undefined => {
  if (e === 0)
    return { draw: (p, W, H) => {
      // orange glass globe on a long cord
      const cx = W / 2;
      const cy = H / 2;
      p.line([cx, cy, 26], [cx, cy, 21], [40, 30, 30]);
      p.box(cx - 1.6, cy - 1.6, 18, 3.2, 3.2, 3, [222, 132, 52], { glowLeft: WARM, top: [240, 170, 90] });
    }, light: { x: 6, y: 6, z: 16, color: [1.15, 0.8, 0.5], radius: 8, strength: 0.7 } };
  if (e === 3)
    return { draw: (p, W, H) => {
      // black industrial pendant, wide and shallow, bright underneath
      const cx = W / 2;
      const cy = H / 2;
      p.line([cx, cy, 26], [cx, cy, 20.5], [40, 30, 30]);
      p.box(cx - 0.6, cy - 0.6, 19.6, 1.2, 1.2, 0.9, SLATE, { outline: false });
      p.box(cx - 2, cy - 2, 18.4, 4, 4, 1.2, SLATE, { glowLeft: [90, 84, 70] });
      p.quad([[cx - 1.6, cy - 1.6, 18.4], [cx + 1.6, cy - 1.6, 18.4], [cx + 1.6, cy + 1.6, 18.4], [cx - 1.6, cy + 1.6, 18.4]], WARM, WARM);
    }, light: { x: 6, y: 6, z: 16, color: [1.05, 0.95, 0.8], radius: 8, strength: 0.7 } };
  return undefined;
};

const plainBox = (c: RGB, h: number, handle: RGB, lines: number[]): Draw => (p, W, H) => {
  p.box(0.6, 0.6, 0, W - 1.2, H - 1.2, h, c);
  for (const z of lines) p.line([1.2, H - 0.59, z], [W - 1.2, H - 0.59, z], shade(c, 0.6));
  p.line([W - 1.6, H - 0.59, h * 0.4], [W - 1.6, H - 0.59, h * 0.8], handle);
};

const sofaIn = (seat: RGB, back: RGB, cushion?: RGB): Draw => (p, W, H) => {
  p.box(0.5, 1.5, 0, W - 1, H - 2, 3.5, seat);
  p.box(0.5, H - 1.5, 0, W - 1, 1, 7, back);
  p.box(0.5, 1.5, 0, 1.4, H - 2, 5.5, back);
  p.box(W - 1.9, 1.5, 0, 1.4, H - 2, 5.5, back);
  if (cushion) p.box(2.4, H - 2.6, 3.5, 2.2, 1, 2.4, cushion);
};

const rugIn = (cs: RGB[]): Draw => (p, W, H, seed) => {
  const c = pickC(cs, seed);
  p.floor(0.5, 0.5, W - 1, H - 1, c);
  p.floor(1.5, 1.5, W - 3, H - 3, shade(c, 1.15));
};

/** Per-piece era looks. Return undefined for an era that keeps the default drawing. */
const ERA_LOOKS: Record<string, (e: FurnitureEra) => Draw | EraLook | undefined> = {
  desk: (e) =>
    e === 0 ? { draw: typewriterDesk }
    : e === 1 ? { draw: (p, W, H) => { table(WOOD)(p, W, H, 0); beigeComputer(p, 2.4); p.box(8.6, 1.4, 5, 2, 2.4, 1, PAPER); }, light: screenLight([0.6, 1.0, 0.7]) }
    : e === 2 ? { draw: (p, W, H) => { table([196, 160, 116])(p, W, H, 0); towerPc(p, false); }, light: screenLight([0.6, 0.8, 1.0]) }
    : { draw: (p, W, H) => { table(LAMINATE)(p, W, H, 0); flatScreen(p, 1.4, 0.6, 5.4, 3.6); laptop(p, 7.6, 2.6); mug(p, 0.8, 3.8); }, light: screenLight([0.7, 0.9, 1.05], 5, 0.3) },
  monitor_desk: (e) =>
    e <= 1 ? { draw: (p, W, H) => {
      // beige computer and a dot-matrix printer feeding fan-fold paper
      table(WOOD)(p, W, H, 0);
      beigeComputer(p, 1);
      p.box(7.4, 1.2, 5, 3.6, 2.4, 1.4, BEIGE);
      p.panelY(7.8, 1.6, 6.4, 2.8, 2.2, PAPER);
      for (let k = 0; k < 2; k++) p.line([7.9, 1.62, 7 + k * 0.8], [10.4, 1.62, 7 + k * 0.8], [160, 170, 190]);
    }, light: screenLight([0.6, 1.0, 0.7]) }
    : e === 2 ? { draw: (p, W, H) => { table(WOOD)(p, W, H, 0); towerPc(p, true); for (const x of [1, 7.4]) p.box(x, 1.4, 5, 0.9, 0.9, 2, [60, 62, 70]); }, light: screenLight([0.6, 0.8, 1.0], 5, 0.3) }
    : { draw: (p, W, H) => {
      // two slim monitors side by side
      table(LAMINATE)(p, W, H, 0);
      flatScreen(p, 0.8, 0.6, 5, 3.6);
      flatScreen(p, 6, 0.6, 5, 3.6);
      keyboard(p, 3.4, 3.8, 5, LAMINATE);
    }, light: { x: 6, y: 2, z: 7, color: [0.65, 0.88, 1.08], radius: 7, strength: 0.32 } },
  phone_desk: eraPhoneDesk,
  lamp_desk: eraLampDesk,
  lamp_floor: eraLampFloor,
  lamp_hanging: eraLampHanging,
  chair: eraChair,
  poster: eraPoster,
  coffee_machine: eraCoffee,
  water_cooler: eraCooler,
  whiteboard: eraWhiteboard,
  till: eraTill,
  sign: eraSign,
  bookshelf: (e) => shelf(shelfFrame[e], 3, 16, eraBooks(e)),
  games_shelf: (e) => shelf(e === 3 ? LAMINATE : e === 0 ? TEAK : [90, 58, 40], 2, 11, eraGameBoxes(e)),
  mini_shelf: (e) => (e === 2 ? undefined : shelf(e === 3 ? LAMINATE : [90, 58, 40], 2, 10, minisIn(ERA_BOXES[e]))),
  display_stand: (e) => (e === 2 ? undefined : displayStand(ERA_BOXES[e])),
  display_case: (e) => (e === 2 ? undefined : displayCase(ERA_BOXES[e])),
  print_rack: (e) => (e === 2 ? undefined : printRack(ERA_BOXES[e])),
  pallet: (e) => (e === 2 ? undefined : palletOf(ERA_BOXES[e])),
  shelf_rack: (e) => (e === 2 ? undefined : shelfRack(ERA_BOXES[e])),
  trolley: (e) => (e === 2 ? undefined : trolleyOf(ERA_BOXES[e])),
  packing_table: (e) => (e === 2 ? undefined : packingTable(ERA_BOXES[e])),
  conveyor: (e) => (e === 2 ? undefined : conveyorOf(ERA_BOXES[e])),
  filing_cabinet: (e) =>
    e === 2 ? undefined
    : plainBox(e === 0 ? [126, 130, 90] : e === 1 ? [200, 192, 166] : [228, 230, 232], 8, e === 3 ? [150, 156, 170] : CHROME, [2, 4.4, 6.8]),
  fridge: (e) =>
    e === 2 ? undefined
    : e === 3 ? (p, W, H) => {
      // stainless double-door with an ice-and-water panel
      p.box(0.6, 0.6, 0, W - 1.2, H - 1.2, 15, [178, 184, 192]);
      p.line([W / 2, H - 0.59, 0.5], [W / 2, H - 0.59, 14.5], [110, 116, 126]);
      p.panelY(1.2, H - 0.58, 8, 1.2, 2.2, [52, 56, 66]);
      p.panelY(1.4, H - 0.57, 9.6, 0.8, 0.4, [90, 180, 255], [90, 180, 255]);
    }
    : plainBox(e === 0 ? [164, 172, 104] : [224, 214, 186], 15, [120, 126, 140], [10]),
  sofa: (e) =>
    e === 0 ? sofaIn([200, 116, 54], [172, 94, 44])
    : e === 1 ? sofaIn([64, 150, 150], [48, 122, 124], [236, 64, 140])
    : e === 3 ? sofaIn([140, 144, 150], [118, 122, 130], [222, 172, 64])
    : undefined,
  rug: (e) =>
    e === 0 ? rugIn([[196, 108, 44], [150, 100, 52], [190, 160, 62]])
    : e === 1 ? rugIn([[150, 44, 120], [40, 92, 170], [30, 140, 140]])
    : e === 3 ? rugIn([[196, 192, 182], [118, 128, 138], [176, 150, 120]])
    : undefined,
  clipboard_stand: (e) =>
    e === 3 ? (p, W, H) => {
      // a tablet on a stand instead of a clipboard
      p.line([W / 2, H / 2, 0], [W / 2, H / 2, 6], SLATE);
      p.panelY(W / 2 - 1.2, H / 2, 6, 2.4, 3.2, SLATE);
      p.panelY(W / 2 - 0.9, H / 2 + 0.02, 6.3, 1.8, 2.6, SCREEN, SCREEN);
    }
    : undefined,
  drafting_table: (e) =>
    e === 3 ? (p, W, H) => {
      // a big pen display tilted on the drafting stand
      legs(p, W, H, 5, SLATE);
      p.quad([[0.5, 0.5, 5], [W - 0.5, 0.5, 7.5], [W - 0.5, H - 0.5, 7.5], [0.5, H - 0.5, 5]], LAMINATE);
      p.quad([[1.5, 1, 5.6], [W - 1.5, 1, 7.3], [W - 1.5, H - 1, 7.3], [1.5, H - 1, 5.6]], SLATE);
      p.quad([[2, 1.4, 5.9], [W - 2, 1.4, 7.2], [W - 2, H - 1.4, 7.2], [2, H - 1.4, 5.9]], SCREEN, SCREEN);
    }
    : undefined,
};

const cache = new Map<string, IsoSprite>();

/**
 * A furniture sprite. rot = 1 turns it a quarter (footprint w/h swap, image mirrors).
 * year (optional) picks the decade's look where the piece has one (typewriter desk in 1970, flat screens in 2020);
 * without a year, or for a piece with no era look, it draws the timeless default.
 */
export function furnitureSprite(kind: string, rot = 0, seed = 0, year?: number): IsoSprite {
  const k = C.furniture[kind];
  const spec = FURNITURE[kind];
  if (!k || !spec) throw new Error(`no furniture ${kind}`);
  const era = year === undefined ? undefined : furnitureEra(year);
  const raw = era === undefined ? undefined : ERA_LOOKS[kind]?.(era);
  const look: EraLook | undefined = typeof raw === "function" ? { draw: raw } : raw;
  const key = `${kind}:${rot}:${seed}:${look ? era : "-"}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const p = spritePainter(k.w, k.h, spec.z + 4);
  (look?.draw ?? spec.draw)(p, k.w * T, k.h * T, seed);
  const light = look && look.light !== undefined ? (look.light ?? undefined) : spec.light;
  let img = p.img;
  let em = p.em;
  let ax = p.ox;
  if (rot % 2 === 1) {
    img = mirror(img);
    em = mirror(em);
    ax = img.w - 1 - p.ox;
  }
  const lights = light ? [{ ...light, x: rot % 2 ? light.y : light.x, y: rot % 2 ? light.x : light.y }] : undefined;
  const sprite: IsoSprite = { img, em: em.opaqueCount() ? em : undefined, ax, ay: p.oy, w: rot % 2 ? k.h : k.w, h: rot % 2 ? k.w : k.h, lights };
  cache.set(key, sprite);
  return sprite;
}

function mirror(b: Bitmap): Bitmap {
  const out = new Bitmap(b.w, b.h);
  out.blit(b, 0, 0, true);
  return out;
}

export function furnitureKinds(): string[] {
  return kinds.map((k) => k.id);
}

export function colorOf(h: string): RGB {
  return hex(h);
}

export { OUTLINE };
